import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
  Archive,
  ArchiveRestore,
  Bot,
  Copy,
  FlaskConical,
  MessageCircle,
  Pencil,
  Plug,
  Plus,
  Sparkles,
  Tags,
  Trash2,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { AgentSummary } from "@/api/generated/AgentSummary"
import { useAgents, useAgentTags, useCreateAgent } from "@/api/queries/agents"
import { useModels } from "@/api/queries/providers"
import { createColumns, DataTable } from "@/components/app/data-table"
import { SelectField, TextareaField, TextField } from "@/components/app/fields"
import { FormDialog } from "@/components/app/form"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useAgentActions } from "@/features/studio/agent-actions"
import { AgentAvatar } from "@/features/studio/agent-avatar"
import { fire } from "@/features/studio/fire"
import { TagList, TagManagerDialog } from "@/features/studio/tags"
import { useDebounced } from "@/features/studio/use-debounced"
import { formatNumber } from "@/lib/format"
import { z } from "@/lib/zod"

interface AgentsSearch {
  q?: string
  tag?: number
  archived?: boolean
  new?: boolean
}

export const Route = createFileRoute("/_app/studio/agents/")({
  staticData: { titleKey: "nav.items.agents" },
  validateSearch: (s: Record<string, unknown>): AgentsSearch => ({
    q: typeof s.q === "string" && s.q ? s.q : undefined,
    tag: Number(s.tag) > 0 ? Number(s.tag) : undefined,
    archived: s.archived === true || s.archived === "true" ? true : undefined,
    new: s.new === true || s.new === "true" ? true : undefined,
  }),
  component: AgentsPage,
})

function AgentsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [query, setQuery] = useState(search.q ?? "")
  const debounced = useDebounced(query, 250)
  const [tagsOpen, setTagsOpen] = useState(false)
  const agents = useAgents({ q: search.q, tag_id: search.tag, include_archived: search.archived })
  const tags = useAgentTags()
  const actions = useAgentActions()

  const setSearch = (patch: Partial<AgentsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to the debounced value
  useEffect(() => {
    if ((debounced || undefined) !== search.q) setSearch({ q: debounced || undefined })
  }, [debounced])

  const open = (a: Pick<AgentSummary, "id">, tab?: "tools" | "skills") =>
    void navigate({
      to: "/studio/agents/$agentId",
      params: { agentId: String(a.id) },
      search: tab ? { tab } : {},
    })

  const columns = useMemo(
    () =>
      createColumns<AgentSummary>((c) => [
        c.accessor("name", {
          header: t("studio.agents.columns.agent"),
          enableHiding: false,
          cell: ({ row }) => {
            const a = row.original
            return (
              <div className="flex max-w-md min-w-56 items-center gap-3">
                <AgentAvatar name={a.name} icon={a.icon} archived={!!a.archived_at} />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={
                        a.archived_at
                          ? "text-muted-foreground truncate font-medium line-through"
                          : "truncate font-medium"
                      }
                    >
                      {a.name}
                    </span>
                    {a.allow_as_chat_agent && !a.archived_at && (
                      <Tooltip>
                        <TooltipTrigger render={<span className="text-primary shrink-0" />}>
                          <MessageCircle className="size-3.5" aria-label={t("studio.agents.chatEnabled")} />
                        </TooltipTrigger>
                        <TooltipContent>{t("studio.agents.chatEnabled")}</TooltipContent>
                      </Tooltip>
                    )}
                    {a.archived_at && (
                      <StatusBadge tone="warning">{t("studio.agents.archivedBadge")}</StatusBadge>
                    )}
                  </div>
                  <div className="text-muted-foreground truncate text-xs">
                    {a.description || t("studio.agents.noDescription")}
                  </div>
                </div>
              </div>
            )
          },
        }),
        c.accessor("model_name", {
          header: t("studio.agents.columns.model"),
          cell: ({ getValue }) => <span className="text-[0.8125rem]">{getValue()}</span>,
        }),
        c.accessor((a) => a.tag_ids.length, {
          id: "tags",
          header: t("studio.agents.columns.tags"),
          meta: { label: t("studio.agents.columns.tags") },
          enableSorting: false,
          cell: ({ row }) => <TagList ids={row.original.tag_ids} tags={tags.data} />,
        }),
        c.accessor((a) => a.mcp_count + a.skill_count, {
          id: "capabilities",
          header: t("studio.agents.columns.capabilities"),
          meta: { label: t("studio.agents.columns.capabilities") },
          cell: ({ row }) => (
            <div className="text-muted-foreground flex items-center gap-3 text-xs tabular-nums">
              <span
                className="inline-flex items-center gap-1"
                title={t("studio.agents.integrationCount", { count: row.original.mcp_count })}
              >
                <Plug className="size-3.5" />
                {row.original.mcp_count}
              </span>
              <span
                className="inline-flex items-center gap-1"
                title={t("studio.agents.skillCount", { count: row.original.skill_count })}
              >
                <Sparkles className="size-3.5" />
                {row.original.skill_count}
              </span>
            </div>
          ),
        }),
        c.accessor("runs_7d", {
          header: t("studio.agents.columns.runs7d"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
        c.accessor("last_run_at", {
          header: t("studio.agents.columns.lastRun"),
          sortFn: "datetime",
          cell: ({ getValue }) => (
            <RelativeTime
              value={getValue()}
              className="text-muted-foreground"
              fallback={t("studio.agents.neverRun")}
            />
          ),
        }),
      ]),
    [t, tags.data],
  )

  const filtered = !!(search.q || search.tag)

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.agents.title")}
        description={t("studio.agents.description")}
        icon={Bot}
        actions={
          <>
            <Button variant="outline" onClick={() => setTagsOpen(true)}>
              <Tags />
              {t("studio.tags.manage")}
            </Button>
            <Button onClick={() => setSearch({ new: true })} data-testid="new-agent">
              <Plus />
              {t("studio.agents.new")}
            </Button>
          </>
        }
      />
      <DataTable
        label={t("studio.agents.title")}
        data={agents.data}
        columns={columns}
        getRowId={(a) => String(a.id)}
        loading={agents.isPending}
        error={agents.error}
        onRetry={() => void agents.refetch()}
        searchable
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder={t("studio.agents.search")}
        initialSorting={[{ id: "name", desc: false }]}
        columnToggle
        persistKey="studio-agents"
        onRowClick={(a) => open(a)}
        toolbar={
          <>
            {(tags.data?.length ?? 0) > 0 && (
              <Select
                value={search.tag != null ? String(search.tag) : "all"}
                onValueChange={(v) => setSearch({ tag: v === "all" || v == null ? undefined : Number(v) })}
              >
                <SelectTrigger size="sm" className="h-8 w-40" aria-label={t("studio.agents.filterTag")}>
                  <SelectValue>
                    {(v: string) =>
                      v === "all"
                        ? t("studio.agents.allTags")
                        : (tags.data?.find((x) => String(x.id) === v)?.name ?? v)
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("studio.agents.allTags")}</SelectItem>
                  {tags.data?.map((tag) => (
                    <SelectItem key={tag.id} value={String(tag.id)}>
                      <span className="size-2 rounded-full" style={{ backgroundColor: tag.color }} />
                      {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Label className="text-muted-foreground gap-2 text-xs font-normal">
              <Switch
                size="sm"
                checked={!!search.archived}
                onCheckedChange={(v) => setSearch({ archived: v || undefined })}
              />
              {t("studio.agents.showArchived")}
            </Label>
          </>
        }
        rowActions={(a) => (
          <>
            <DropdownMenuItem onClick={() => open(a)}>
              <Pencil />
              {t("studio.agents.actions.edit")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                void navigate({
                  to: "/studio/agents/$agentId",
                  params: { agentId: String(a.id) },
                  search: { try: true },
                })
              }
              disabled={!!a.archived_at}
            >
              <FlaskConical />
              {t("studio.agents.actions.try")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => fire(actions.duplicate(a))}>
              <Copy />
              {t("studio.agents.actions.duplicate")}
            </DropdownMenuItem>
            {a.archived_at ? (
              <DropdownMenuItem onClick={() => fire(actions.unarchive(a))}>
                <ArchiveRestore />
                {t("studio.agents.actions.unarchive")}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => void actions.archive(a)}>
                <Archive />
                {t("studio.agents.actions.archive")}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={() => void actions.remove(a)}>
              <Trash2 />
              {t("studio.agents.actions.delete")}
            </DropdownMenuItem>
          </>
        )}
        emptyState={
          filtered ? (
            <EmptyState
              variant="plain"
              icon={Bot}
              title={t("studio.agents.emptyFiltered")}
              action={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setQuery("")
                    setSearch({ q: undefined, tag: undefined })
                  }}
                >
                  {t("studio.agents.clearFilters")}
                </Button>
              }
            />
          ) : (
            <EmptyState
              variant="plain"
              icon={Bot}
              title={t("studio.agents.empty.title")}
              description={t("studio.agents.empty.description")}
              action={
                <Button size="sm" onClick={() => setSearch({ new: true })}>
                  <Plus />
                  {t("studio.agents.empty.action")}
                </Button>
              }
            />
          )
        }
      />
      <TagManagerDialog open={tagsOpen} onOpenChange={setTagsOpen} />
      <NewAgentDialog
        open={!!search.new}
        onOpenChange={(o) => !o && setSearch({ new: undefined })}
        onCreated={(id) =>
          void navigate({ to: "/studio/agents/$agentId", params: { agentId: String(id) }, search: {} })
        }
      />
    </PageContainer>
  )
}

const newAgentSchema = z.object({
  name: z.string().trim().min(1).max(255),
  model_id: z
    .number({ error: () => undefined })
    .int()
    .positive(),
  description: z.string().max(2000),
})
type NewAgentValues = z.infer<typeof newAgentSchema>

function NewAgentDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  onCreated: (id: number) => void
}) {
  const { t } = useTranslation()
  const models = useModels()
  const create = useCreateAgent()
  const form = useForm<NewAgentValues>({
    resolver: zodResolver(newAgentSchema),
    defaultValues: { name: "", model_id: undefined as unknown as number, description: "" },
  })
  const firstModel = models.data?.[0]?.id
  useEffect(() => {
    if (open) form.reset({ name: "", model_id: firstModel as number, description: "" })
  }, [open, firstModel, form])

  const noModels = models.isSuccess && models.data.length === 0
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      title={t("studio.agents.newTitle")}
      description={t("studio.agents.newDescription")}
      submitLabel={t("studio.agents.createAndConfigure")}
      onSubmit={async (v) => {
        const a = await create.mutateAsync({
          name: v.name.trim(),
          model_id: v.model_id,
          description: v.description,
          system_prompt: t("studio.agents.defaultPrompt", { name: v.name.trim() }),
        })
        onOpenChange(false)
        onCreated(a.id)
      }}
    >
      {noModels ? (
        <EmptyState
          size="sm"
          title={t("studio.agents.noModels.title")}
          description={t("studio.agents.noModels.description")}
          action={
            <Button size="sm" nativeButton={false} render={<Link to="/studio/models" />}>
              {t("studio.agents.noModels.action")}
            </Button>
          }
        />
      ) : (
        <>
          <TextField
            control={form.control}
            name="name"
            label={t("studio.agents.fields.name")}
            required
            autoFocus
          />
          <SelectField
            control={form.control}
            name="model_id"
            label={t("studio.agents.fields.model")}
            required
            options={(models.data ?? []).map((m) => ({
              value: m.id,
              label: m.name,
              description: `${m.provider_name} · ${m.model}`,
            }))}
          />
          <TextareaField
            control={form.control}
            name="description"
            label={t("studio.agents.fields.description")}
            rows={2}
            placeholder={t("studio.agents.fields.descriptionPlaceholder")}
          />
        </>
      )}
    </FormDialog>
  )
}
